import {
    Box,
    Checkbox,
    ClickAwayListener,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Popper,
    Tooltip,
} from '@mui/material';
import React, { useRef, useState } from 'react';

import ClearColor from '@mui/icons-material/DoDisturb';
import ColorPicked from '@mui/icons-material/Square';
import Locale from '../Locale';
import LocalizedTooltip from '../LocalizedTooltip';
import NoColorPicked from '@mui/icons-material/CropFree';
import PropTypes from 'prop-types';
import { SketchPicker } from 'react-color';
import palette from '../../theme/palette';
import { red } from '../../colors';
import useBoundCallback from '../../helpers/Hooks/useBoundCallback';
import { v4 as uuid } from 'uuid';
import { withFormikField } from './FormikFields';

/**
 * @typedef {Object} Base
 * @property {Object=} translationOptions The `options` object provided to all internal <Locale/> translations for the TextField
 * @property {boolean} disabled Disabled the color picker
 * @property {string} helperText The helper text for the color picker
 * @property {string} label The label for the color picker
 * @property {(color: string) => undefined} onChange The callback function that is executed each time a color is selected
 * @property {boolean} value The provided value for the color picker
 *
 * @typedef {Base & Pick<import('@mui/material').FormControlProps, 'error' | 'fullWidth' | 'required'> } ColorPickerProps
 */

/**
 * @type {React.ForwardRefExoticComponent<ColorPickerProps>}
 */
const ColorPicker = React.forwardRef((props, ref) => {
    const {
        allowClear = true,
        'aria-describedby': ariaDescribedBy,
        disabled,
        error,
        FormHelperTextProps,
        fullWidth,
        helperText,
        label,
        onChange,
        required,
        translationOptions,
        value,
    } = props;

    const errorId = useRef(uuid());
    const containerRef = useRef(null);
    // ColorPicker can never be uncontrolled, so we need to keep track of the date internally so it updates correctly
    const [color, setValue] = React.useState(value || '');
    const [showPicker, setShowPicker] = useState(false);

    const handlePickerChange = useBoundCallback(
        (change, disabled, c) => {
            if (c.source === 'hex' && c.hex === 'transparent') {
                setValue('');
                !disabled && change && change(null);
                setShowPicker(false);
                return;
            }
            const { r, g, b, a } = c.rgb;
            const value = a < 1 ? `rgba(${r},${g},${b},${a})` : c.hex;
            setValue(`${value}`);
            !disabled && change && change(`${value}`);
        },
        [onChange, disabled]
    );

    const handlePickerOpen = () => {
        !disabled && setShowPicker(true);
    };
    const handlePickerClose = () => {
        setShowPicker(false);
    };

    const checked = !!`${color || ''}`.replace(/\s/g, '');
    const presetColors = allowClear
        ? [
              {
                  color: 'rgba(0,0,0,0)',
                  title: '',
                  children: (
                      <LocalizedTooltip title="Clear">
                          <ClearColor
                              sx={{
                                  color: red,
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  fontSize: 14,
                              }}
                          />
                      </LocalizedTooltip>
                  ),
              },
          ]
        : [];

    React.useLayoutEffect(() => {
        setValue(value || '');
    }, [value]);

    return (
        <FormControl fullWidth={!!fullWidth} error={error} required={!!required}>
            <ClickAwayListener onClickAway={handlePickerClose}>
                <Box ref={containerRef}>
                    <FormControlLabel
                        key={value}
                        label={
                            !label || React.isValidElement(label) ? (
                                label
                            ) : (
                                <Locale path={label} options={translationOptions} />
                            )
                        }
                        onClick={handlePickerOpen}
                        control={
                            <Tooltip title={color}>
                                <Checkbox
                                    checked={checked}
                                    icon={<NoColorPicked />}
                                    checkedIcon={<ColorPicked stroke={palette.common.muted} />}
                                    sx={{
                                        py: 0,
                                        '&.Mui-checked': {
                                            color,
                                        },
                                    }}
                                    aria-describedby={
                                        !helperText // If there is an error, the aria-describedby needs to reference the error message, and that's handled by MUI internally
                                            ? ariaDescribedBy
                                            : errorId.current
                                    }
                                />
                            </Tooltip>
                        }
                        sx={{ alignItems: 'flex-start' }}
                    />
                    <Popper
                        anchorEl={containerRef.current}
                        placement="bottom"
                        open={showPicker}
                        modifiers={[
                            {
                                name: 'flip',
                                enabled: true,
                                options: {
                                    altBoundary: true,
                                    rootBoundary: 'document',
                                    padding: 8,
                                },
                            },
                            {
                                name: 'preventOverflow',
                                enabled: true,
                                options: {
                                    altAxis: true,
                                    altBoundary: true,
                                    tether: true,
                                    rootBoundary: 'document',
                                    padding: 8,
                                },
                            },
                        ]}
                    >
                        <Box
                            sx={{
                                position: 'fixed',
                                top: 0,
                                right: 0,
                                bottom: 0,
                                left: 0,
                            }}
                            onClick={handlePickerClose}
                        />
                        <SketchPicker
                            ref={ref}
                            color={color}
                            onChange={handlePickerChange}
                            presetColors={presetColors}
                        />
                    </Popper>
                </Box>
            </ClickAwayListener>
            <FormHelperText
                {...FormHelperTextProps}
                id={errorId.current}
                sx={{ whiteSpace: 'pre-line', ...(FormHelperTextProps && FormHelperTextProps.sx) }}
            >
                {helperText}
            </FormHelperText>
        </FormControl>
    );
});

ColorPicker.propTypes = {
    allowClear: PropTypes.bool,
    'aria-describedby': PropTypes.string,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    FormHelperTextProps: PropTypes.object,
    fullWidth: PropTypes.bool,
    helperText: PropTypes.any,
    label: PropTypes.string,
    onChange: PropTypes.func,
    required: PropTypes.bool,
    translationOptions: PropTypes.object,
    value: PropTypes.string,
};

export default ColorPicker;

export const FormikColorPicker = withFormikField(ColorPicker);
FormikColorPicker.displayName = 'FormikColorPicker';
